import React, { useState, useEffect } from "react";

function EVCalculator() {
  // Set default values
  const [gasPrice, setGasPrice] = useState(300);
  const [fuelEfficiency, setFuelEfficiency] = useState(7);
  const [electricPrice, setElectricPrice] = useState(22);
  const [yearlyDistance, setYearlyDistance] = useState(12000);
  const [consumptionWh, setConsumptionWh] = useState(166); // Wh/km

  // New state for free electricity toggle
  const [freeElectricity, setFreeElectricity] = useState(false);

  // New state for purchase difference option
  const [showPurchaseDiff, setShowPurchaseDiff] = useState(false);
  const [priceDifference, setPriceDifference] = useState(500000);
  const [interestRate, setInterestRate] = useState(8);
  const [depreciation, setDepreciation] = useState(10);

  // New state for EV discount option
  const [applyDiscount, setApplyDiscount] = useState(false);
  const discountPerKm = 4; // 4 kr per km discount

  // Fast charging fixed fee for electric vehicles
  const fixedElectricFee = 6;
  // Charging efficiency: 95%
  const chargingEfficiency = 0.95;
  // Convert Wh/km to kWh/km for calculations
  const consumption = consumptionWh / 1000; // kWh per km
  // Calculate km per kWh for display
  const kmPerKWh = consumption > 0 ? (1 / consumption).toFixed(2) : "–";

  // Use free electricity flag to override electric price if enabled
  const effectiveElectricPrice = freeElectricity ? 0 : electricPrice;
  // Calculate cost per km for electric vehicles and apply discount if enabled
  const baseEvCostPerKm =
    (consumption / chargingEfficiency) * effectiveElectricPrice + fixedElectricFee;
  const effectiveEvCostPerKm = baseEvCostPerKm - (applyDiscount ? discountPerKm : 0);

  // Calculate cost per km for gas vehicles
  const gasCostPerKm = (fuelEfficiency / 100) * gasPrice;

  // Calculate yearly costs
  const gasYearlyCost = gasCostPerKm * yearlyDistance;
  const evYearlyCost = effectiveEvCostPerKm * yearlyDistance;

  // Extra cost per year for purchase difference option
  const extraYearlyCost = showPurchaseDiff
    ? priceDifference * ((interestRate + depreciation) / 100)
    : 0;
  const finalEvYearlyCost = evYearlyCost + extraYearlyCost;
  const finalEvCostPerKm = finalEvYearlyCost / yearlyDistance;

  const savings = Math.abs(gasYearlyCost - finalEvYearlyCost);
  const cheaperOption = gasYearlyCost < finalEvYearlyCost ? "Bensín" : "Rafmagn";

  // Helper function to format numbers with Icelandic thousands separator
  const formatNumber = (num) =>
    new Intl.NumberFormat("is-IS").format(Math.round(num)).replace(/,/g, '.');

  // PWA detection remains if needed for styling
  const [isPWA, setIsPWA] = useState(false);
  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      setIsPWA(true);
    }
  }, []);

  // Uniform color palette & enhanced interaction styles
  // Primary color: blue; Active/toggled state: green.
  const sliderClasses =
    "w-4/5 md:w-full ml-auto transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400";
  const buttonBaseClasses =
    "mt-2 px-4 py-2 rounded w-full md:full transition-colors focus:outline-none focus:ring-2";
  const freeButtonClasses = freeElectricity
    ? `${buttonBaseClasses} bg-green-600 hover:bg-green-700 text-white focus:ring-green-400`
    : `${buttonBaseClasses} bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-400`;
  const discountButtonClasses = applyDiscount
    ? `${buttonBaseClasses} bg-green-600 hover:bg-green-700 text-white focus:ring-green-400`
    : `${buttonBaseClasses} bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-400`;

  return (
    <div className={`flex flex-col items-center p-6 font-sans ${isPWA ? "bg-gray-100 min-h-screen" : "bg-gray-50"}`}>
      <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">
        Reiknivél: Rafmagnsbíll vs Bensínbíll
      </h1>
      <p className="text-center text-gray-600 mb-6">
        Rafmagnsbílar bera fast gjald upp á {fixedElectricFee} ISK/km.
      </p>
      <div className="p-6 w-full max-w-lg bg-white shadow-md rounded-lg grid gap-6">
        <div>
          <label className="block text-lg font-medium text-gray-800 cursor-pointer">
            Keyrt á ári (km)
          </label>
          <input
            type="range"
            min="5000"
            max="50000"
            step="500"
            value={yearlyDistance}
            onChange={(e) => setYearlyDistance(Number(e.target.value))}
            className={sliderClasses}
            aria-label="Árs keyrsla í km"
          />
          <p className="text-gray-600 mt-1">{yearlyDistance} km</p>
        </div>

        <div>
          <label className="block text-lg font-medium text-gray-800 cursor-pointer">
            Bensínverð (ISK/Lítra)
          </label>
          <input
            type="range"
            min="250"
            max="350"
            step="1"
            value={gasPrice}
            onChange={(e) => setGasPrice(Number(e.target.value))}
            className={sliderClasses}
            aria-label="Bensínverð í ISK á lítra"
          />
          <p className="text-gray-600 mt-1">{gasPrice} ISK</p>
        </div>

        <div>
          <label className="block text-lg font-medium text-gray-800 cursor-pointer">
            Eldsneytiseyðsla (L/100 km)
          </label>
          <input
            type="range"
            min="5"
            max="20"
            step="1"
            value={fuelEfficiency}
            onChange={(e) => setFuelEfficiency(Number(e.target.value))}
            className={sliderClasses}
            aria-label="Eldsneytiseyðsla í L/100 km"
          />
          <p className="text-gray-600 mt-1">{fuelEfficiency} L/100km</p>
        </div>

        <div>
          <label className="block text-lg font-medium text-gray-800 cursor-pointer">
            Rafmagnsverð (ISK/kWh) <br />
            <span className="text-sm text-gray-600">
              Venjulegt rafmagnsverð er um 10 kr og dreifingarverð um 10 kr.
            </span>
          </label>
          <input
            type="range"
            min="10"
            max="50"
            step="1"
            value={electricPrice}
            onChange={(e) => setElectricPrice(Number(e.target.value))}
            className={sliderClasses}
            disabled={freeElectricity}
            aria-label="Rafmagnsverð í ISK á kWh"
          />
          <p className="text-gray-600 mt-1">
            {freeElectricity ? "Ókeypis Rafmagn" : `${electricPrice} ISK`}
          </p>
          <div className="flex flex-col md:flex-row gap-2 mt-2">
            <button
              className={`${buttonBaseClasses} bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-400`}
              onClick={() => setElectricPrice(60)}
              disabled={freeElectricity}
            >
              Hraðhleðsla (60 ISK/kWh)
            </button>
            <button
              className={freeButtonClasses}
              onClick={() => setFreeElectricity(!freeElectricity)}
            >
              {freeElectricity ? "Borga" : "Ókeypis"}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-lg font-medium text-gray-800 cursor-pointer">
            Rafmagnsbíls orkunotkun (Wh/km)
          </label>
          <input
            type="range"
            min="100"
            max="300"
            step="1"
            value={consumptionWh}
            onChange={(e) => setConsumptionWh(Number(e.target.value))}
            className={sliderClasses}
            aria-label="Orkunotkun í Wh/km"
          />
          <p className="text-gray-600 mt-1">
            {consumptionWh} Wh/km ({kmPerKWh} km/kWh) með 95% hleðsluvirkni.
          </p>
        </div>

        {/* Toggle purchase difference options */}
        <div>
          <button
            className={`${buttonBaseClasses} bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-400`}
            onClick={() => setShowPurchaseDiff(!showPurchaseDiff)}
          >
            Er rafmagnsbíllinn dýrari í innkaupum en bensínbíllinn.
          </button>
        </div>

        {/* Extra inputs for purchase difference */}
        {showPurchaseDiff && (
          <div className="border p-4 rounded bg-gray-50">
            <div>
              <label className="block text-lg font-medium text-gray-800">
                Mismunur í kaupverði (ISK)
              </label>
              <input
                type="range"
                min="250000"
                max="10000000"
                step="100000"
                value={priceDifference}
                onChange={(e) => setPriceDifference(Number(e.target.value))}
                className={sliderClasses}
                aria-label="Mismunur í kaupverði í ISK"
              />
              <p className="text-gray-600 mt-1">
                {formatNumber(priceDifference)} ISK
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-lg font-medium text-gray-800 cursor-pointer">
                Vextir (%)
              </label>
              <input
                type="range"
                min="0"
                max="20"
                step="0.5"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className={sliderClasses}
                aria-label="Vextir í prósentum"
              />
              <p className="text-gray-600 mt-1">{interestRate}%</p>
            </div>

            <div className="mt-4">
              <label className="block text-lg font-medium text-gray-800 cursor-pointer">
                Afskriftir (%)
              </label>
              <input
                type="range"
                min="0"
                max="20"
                step="0.5"
                value={depreciation}
                onChange={(e) => setDepreciation(Number(e.target.value))}
                className={sliderClasses}
                aria-label="Afskriftir í prósentum"
              />
              <p className="text-gray-600 mt-1">{depreciation}%</p>
            </div>
          </div>
        )}

        {/* Toggle EV discount option */}
        <div>
          <button
            className={discountButtonClasses}
            onClick={() => setApplyDiscount(!applyDiscount)}
          >
            {applyDiscount
              ? "Ekki reikna með smurningu og bifreiðagjöldum"
              : "Reikna með smurningu og bifreiðagjöldum"}
          </button>
          {applyDiscount && (
            <p className="text-sm text-gray-600 mt-2">
              Rafmagnsbílar njóta grófáætlað 4 kr/km afsláttar vegna lægra bifreiðargjalda og enginn þörf á smurningu.
            </p>
          )}
        </div>

        <div className="text-xl font-semibold text-center mt-6" aria-live="polite">
          <p>
            Bensínbíll kostar á km:{" "}
            <span className="text-blue-600">{gasCostPerKm.toFixed(1)} ISK</span>
          </p>
          <p>
            Rafmagnsbíll kostar á km:{" "}
            <span className="text-green-600">{finalEvCostPerKm.toFixed(1)} ISK</span>
          </p>
          <p>
            Árleg sparnaður ({cheaperOption} er ódýrari):{" "}
            <span className="text-amber-500">{formatNumber(savings)} ISK</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default EVCalculator;
